import React, { Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon, FolderIcon } from '@heroicons/react/24/outline';
import { useProject } from '../../hooks/useProject';
import LoadingSpinner from './LoadingSpinner';

const ProjectSelector: React.FC = () => {
  const { 
    selectedProject, 
    projects, 
    loading, 
    error, 
    setSelectedProject,
    isProjectSelected 
  } = useProject();

  if (loading) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2">
        <LoadingSpinner size="sm" />
        <span className="text-sm text-gray-500 dark:text-gray-400">Loading projects...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-3 py-2">
        <span className="text-sm text-red-500 dark:text-red-400">Failed to load projects</span>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="px-3 py-2">
        <span className="text-sm text-gray-500 dark:text-gray-400">No projects available</span>
      </div>
    );
  }

  return (
    <div className="w-full max-w-xs">
      <Listbox value={selectedProject} onChange={setSelectedProject}>
        <div className="relative">
          <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white dark:bg-gray-800 py-2 pl-3 pr-10 text-left shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
            <span className="flex items-center">
              <FolderIcon className="h-5 w-5 text-gray-400 mr-2" aria-hidden="true" />
              <span className="block truncate text-gray-900 dark:text-white">
                {selectedProject ? selectedProject.name : 'Select a project'}
              </span>
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-white/10 focus:outline-none sm:text-sm">
              {projects.map((project) => (
                <Listbox.Option
                  key={project._id}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                      active ? 'bg-amber-100 dark:bg-amber-900 text-amber-900 dark:text-amber-100' : 'text-gray-900 dark:text-gray-100'
                    }`
                  }
                  value={project}
                >
                  {({ selected }) => (
                    <>
                      <div className="flex items-center">
                        <FolderIcon className="h-5 w-5 text-gray-400 mr-2" aria-hidden="true" />
                        <span
                          className={`block truncate ${
                            selected ? 'font-medium' : 'font-normal'
                          }`}
                        >
                          {project.name}
                        </span>
                        {project.description && (
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 truncate">
                            {project.description}
                          </span>
                        )}
                      </div>
                      {selected ? (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600 dark:text-amber-400">
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                      <div className="mt-1 flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          project.status === 'active' 
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                            : project.status === 'completed'
                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300'
                            : project.status === 'on-hold'
                            ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                        }`}>
                          {project.status}
                        </span>
                        {(project.members || project.teamMembers) && (
                          <span>
                            {project.members ? project.members.length : project.teamMembers.length} member{(project.members ? project.members.length : project.teamMembers.length) !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
      {selectedProject && (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Selected: {selectedProject.name}
        </div>
      )}
    </div>
  );
};

export default ProjectSelector;